import { useCallback, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";

import { GlobalSearch } from "./GlobalSearch";
import { Sidebar } from "./Sidebar";
import { TopBar } from "./TopBar";
import { Footer, Modal, PageTransition, SkipLink } from "./ui";
import { useKeyboardShortcuts } from "../hooks/useKeyboardShortcuts";
import { useToast } from "../hooks/useToast";
import { ApiError, type ApiUser, logout } from "../lib/api";

interface Props {
  user: ApiUser;
  onLoggedOut: () => void;
  onPrefetchRoute: (path: string) => void;
}

/**
 * ログイン後の共通レイアウト。
 * - 左: 固定サイドバー (モバイルはドロワー)
 * - 上: トップバー (会社 / 検索 / 通知 / ユーザー)
 * - 中央: Outlet にページが入る
 */
export function Layout({ user, onLoggedOut, onPrefetchRoute }: Props): JSX.Element {
  const [loggingOut, setLoggingOut] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [shortcutHelpOpen, setShortcutHelpOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();
  const openSearch = useCallback(() => setSearchOpen(true), []);
  const openShortcutHelp = useCallback(() => setShortcutHelpOpen(true), []);

  useKeyboardShortcuts({ navigate, onOpenHelp: openShortcutHelp, onOpenSearch: openSearch });

  async function handleLogout(): Promise<void> {
    if (loggingOut) return;
    setLoggingOut(true);
    try {
      await logout();
      onLoggedOut();
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : "logout failed";
      toast.error(`ログアウトに失敗しました: ${msg}`);
      setLoggingOut(false);
    }
  }

  return (
    <div className="min-h-screen bg-bg">
      <SkipLink href="#main-content">メインコンテンツへスキップ</SkipLink>
      <Sidebar
        user={user}
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        onPrefetch={onPrefetchRoute}
      />
      <div className="flex min-h-screen flex-col md:pl-60">
        <TopBar
          user={user}
          loggingOut={loggingOut}
          onOpenSearch={openSearch}
          onToggleSidebar={() => setSidebarOpen((v) => !v)}
          onLogout={() => void handleLogout()}
        />
        <main
          id="main-content"
          tabIndex={-1}
          className="flex-1 px-4 py-5 md:px-6 md:py-6"
        >
          <div className="mx-auto w-full max-w-7xl">
            <PageTransition>
              <Outlet />
            </PageTransition>
          </div>
        </main>
        <Footer className="mx-auto w-full max-w-7xl px-4 pb-6 md:px-6" />
      </div>
      <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
      <ShortcutHelpModal open={shortcutHelpOpen} onClose={() => setShortcutHelpOpen(false)} />
    </div>
  );
}

function ShortcutHelpModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}): JSX.Element | null {
  return (
    <Modal open={open} title="Shortcuts" onClose={onClose} className="max-w-lg">
      <div className="space-y-3">
        <ShortcutRow keys="Ctrl/Cmd + K" label="検索を開く" />
        <ShortcutRow keys="g then t" label="今日やること" />
        <ShortcutRow keys="g then c" label="案件一覧" />
        <ShortcutRow keys="g then k" label="顧客" />
        <ShortcutRow keys="g then m" label="メッセージ" />
        <ShortcutRow keys="g then f" label="追客" />
        <ShortcutRow keys="?" label="このヘルプを表示" />
      </div>
    </Modal>
  );
}

function ShortcutRow({ keys, label }: { keys: string; label: string }): JSX.Element {
  return (
    <div className="flex items-center justify-between gap-4 rounded-md border border-border bg-bg px-3 py-2">
      <span className="text-sm text-text-primary">{label}</span>
      <kbd className="shrink-0 rounded border border-border-strong bg-surface px-2 py-1 text-xs text-text-secondary">
        {keys}
      </kbd>
    </div>
  );
}
